import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { getValueAtDescriptorPath } from "../elementEditing";
import { VisualColorControl } from "../inspector/VisualSettingControls";
import {
  FLOATING_WHATSAPP_STYLE_DEFAULTS,
  floatingWhatsappButtonSx,
  normalizeFloatingWhatsappStyle,
} from "../floatingWhatsappVisualOwnership";

function fieldByPath(fields, path) {
  return (fields || []).find((field) => field?.path === path) || null;
}

function fieldValue(form, field, fallback = "") {
  return field ? getValueAtDescriptorPath(form, field.path, fallback) : fallback;
}

function ColorControl({ field, form, disabled, onCommit }) {
  if (!field) return null;
  const fallback = field.defaultValue || "#000000";
  return (
    <VisualColorControl
      label={field.label}
      value={fieldValue(form, field, fallback)}
      fallback={fallback}
      disabled={disabled}
      inheritedLabel="Predeterminado"
      personalizedLabel="Explícito"
      helperText="Este color pertenece sólo al WhatsApp flotante."
      allowExactCss
      exactValueMaxLength={field.maxLength || 100}
      onChange={(next) => onCommit?.(field.path, next)}
      onReset={() => onCommit?.(field.path, fallback)}
    />
  );
}

function NumberControl({ field, form, disabled, onCommit }) {
  if (!field) return null;
  const value = fieldValue(form, field, field.defaultValue ?? "");
  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      label={field.label}
      value={value}
      disabled={disabled}
      inputProps={{
        min: field.limits?.min,
        max: field.limits?.max,
        step: field.limits?.step || 1,
      }}
      onChange={(event) => {
        const raw = event.target.value;
        if (raw === "") return;
        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) return;
        const min = Number(field.limits?.min);
        const max = Number(field.limits?.max);
        const bounded = Math.min(Number.isFinite(max) ? max : parsed, Math.max(Number.isFinite(min) ? min : parsed, parsed));
        onCommit?.(field.path, bounded);
      }}
    />
  );
}

function ShadowControl({ field, form, disabled, onCommit }) {
  if (!field) return null;
  return (
    <TextField
      fullWidth
      size="small"
      label={field.label}
      value={String(fieldValue(form, field, field.defaultValue || "none") || "")}
      disabled={disabled}
      inputProps={{ maxLength: field.maxLength || 300 }}
      helperText="Sombra CSS segura. Ej.: 0 8px 28px rgba(0,0,0,.3)"
      onChange={(event) => onCommit?.(field.path, event.target.value)}
    />
  );
}

export default function FloatingWhatsappElementEditor({ descriptor, form, disabled, onCommit }) {
  const fields = descriptor?.fields || [];
  const visibleField = fieldByPath(fields, "sections.showFloatingWhatsapp");
  const numberField = fieldByPath(fields, "business.whatsapp");
  const messageField = fieldByPath(fields, "contact.whatsappMessage");
  const ariaField = fieldByPath(fields, "contact.floatingWhatsappAriaLabel");

  const styleFields = {
    backgroundColor: fieldByPath(fields, "widgets.floatingWhatsapp.style.backgroundColor"),
    iconColor: fieldByPath(fields, "widgets.floatingWhatsapp.style.iconColor"),
    borderColor: fieldByPath(fields, "widgets.floatingWhatsapp.style.borderColor"),
    borderWidth: fieldByPath(fields, "widgets.floatingWhatsapp.style.borderWidth"),
    borderRadius: fieldByPath(fields, "widgets.floatingWhatsapp.style.borderRadius"),
    boxShadow: fieldByPath(fields, "widgets.floatingWhatsapp.style.boxShadow"),
    size: fieldByPath(fields, "widgets.floatingWhatsapp.style.size"),
    hoverBackgroundColor: fieldByPath(fields, "widgets.floatingWhatsapp.style.hover.backgroundColor"),
    hoverIconColor: fieldByPath(fields, "widgets.floatingWhatsapp.style.hover.iconColor"),
    hoverBorderColor: fieldByPath(fields, "widgets.floatingWhatsapp.style.hover.borderColor"),
    hoverBoxShadow: fieldByPath(fields, "widgets.floatingWhatsapp.style.hover.boxShadow"),
  };

  const visible = visibleField ? getValueAtDescriptorPath(form, visibleField.path, true) !== false : true;
  const number = String(getValueAtDescriptorPath(form, numberField?.path, "") || "");
  const message = String(getValueAtDescriptorPath(form, messageField?.path, "") || "");
  const ariaLabel = String(getValueAtDescriptorPath(form, ariaField?.path, "") || "");
  const configured = /\d/.test(number);
  const effectiveVisible = visible && configured;
  const visibilityLabel = visible ? (configured ? "Visible" : "No se mostrará") : "Oculto";
  const style = normalizeFloatingWhatsappStyle(
    getValueAtDescriptorPath(form, "widgets.floatingWhatsapp.style", FLOATING_WHATSAPP_STYLE_DEFAULTS),
  );

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          p: 1.25,
          border: "1px solid rgba(25,36,47,.10)",
          borderRadius: 2.5,
          bgcolor: "rgba(25,36,47,.025)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Box
            aria-hidden="true"
            sx={{
              ...floatingWhatsappButtonSx(style),
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              opacity: effectiveVisible ? 1 : 0.48,
              transition: "background-color .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease, transform .15s ease",
            }}
          >
            <WhatsAppIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>WhatsApp flotante</Typography>
            <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" sx={{ mt: 0.55 }}>
              <Chip size="small" label={configured ? "WhatsApp configurado" : "Falta número"} variant={configured ? "filled" : "outlined"} />
              <Chip size="small" label={visibilityLabel} variant="outlined" />
            </Stack>
            {visible && !configured ? (
              <Typography variant="caption" sx={{ display: "block", mt: 0.65, color: "rgba(25,36,47,.66)" }}>
                El botón está activado, pero no aparecerá en la web hasta configurar un número de WhatsApp.
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Box>

      {visibleField ? (
        <FormControlLabel
          control={<Switch checked={visible} disabled={disabled} onChange={(event) => onCommit?.(visibleField.path, event.target.checked)} />}
          label="Mostrar botón flotante"
        />
      ) : null}

      {numberField ? (
        <Stack spacing={0.6}>
          <Stack direction="row" spacing={0.7} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Número de WhatsApp</Typography>
            <Chip size="small" label="Dato compartido del negocio" variant="outlined" />
          </Stack>
          <TextField
            fullWidth
            size="small"
            label="Número"
            placeholder="Ej. +54 9 261 555 1234"
            value={number}
            disabled={disabled}
            inputProps={{ maxLength: numberField.maxLength }}
            onChange={(event) => onCommit?.(numberField.path, event.target.value)}
          />
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
            Se usa también en contacto, footer y otros accesos de WhatsApp.
          </Typography>
        </Stack>
      ) : null}

      {messageField ? (
        <Stack spacing={0.6}>
          <Stack direction="row" spacing={0.7} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Mensaje inicial</Typography>
            <Chip size="small" label="Compartido" variant="outlined" />
          </Stack>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Mensaje"
            value={message}
            disabled={disabled}
            inputProps={{ maxLength: messageField.maxLength }}
            onChange={(event) => onCommit?.(messageField.path, event.target.value)}
          />
        </Stack>
      ) : null}

      <Accordion
        defaultExpanded
        disableGutters
        elevation={0}
        sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} aria-controls="floating-whatsapp-appearance-content" id="floating-whatsapp-appearance-header">
          <Stack>
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Apariencia</Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>Estilo exclusivo del widget; no hereda del theme.</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails id="floating-whatsapp-appearance-content">
          <Stack spacing={1.2}>
            <Typography variant="caption" sx={{ fontWeight: 900 }}>Normal</Typography>
            <ColorControl field={styleFields.backgroundColor} form={form} disabled={disabled} onCommit={onCommit} />
            <ColorControl field={styleFields.iconColor} form={form} disabled={disabled} onCommit={onCommit} />
            <ColorControl field={styleFields.borderColor} form={form} disabled={disabled} onCommit={onCommit} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <NumberControl field={styleFields.borderWidth} form={form} disabled={disabled} onCommit={onCommit} />
              <NumberControl field={styleFields.borderRadius} form={form} disabled={disabled} onCommit={onCommit} />
              <NumberControl field={styleFields.size} form={form} disabled={disabled} onCommit={onCommit} />
            </Stack>
            <ShadowControl field={styleFields.boxShadow} form={form} disabled={disabled} onCommit={onCommit} />

            <Typography variant="caption" sx={{ fontWeight: 900, pt: 0.6 }}>Hover</Typography>
            <ColorControl field={styleFields.hoverBackgroundColor} form={form} disabled={disabled} onCommit={onCommit} />
            <ColorControl field={styleFields.hoverIconColor} form={form} disabled={disabled} onCommit={onCommit} />
            <ColorControl field={styleFields.hoverBorderColor} form={form} disabled={disabled} onCommit={onCommit} />
            <ShadowControl field={styleFields.hoverBoxShadow} form={form} disabled={disabled} onCommit={onCommit} />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {ariaField ? (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&:before": { display: "none" } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} aria-controls="floating-whatsapp-accessibility-content" id="floating-whatsapp-accessibility-header">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Accesibilidad</Typography>
              <Chip size="small" label="Opcional" variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails id="floating-whatsapp-accessibility-content">
            <TextField
              fullWidth
              size="small"
              label="Descripción para lectores de pantalla"
              value={ariaLabel}
              disabled={disabled}
              inputProps={{ maxLength: ariaField.maxLength }}
              onChange={(event) => onCommit?.(ariaField.path, event.target.value)}
            />
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
